import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormSheet } from "@/components/ui/FormSheet";
import type { HojaRevision, OrigenRevision } from "@/lib/cuaderno/revision";

const COLOR: Record<OrigenRevision, string> = {
  dato: "#1D7A46",
  calculo: "#2E84DE",
  defecto: "#B7791F",
  usuario: "#6B46C1",
  deducido: "#C05621",
  hueco: "#B93C34",
};
const ORIGENES: OrigenRevision[] = ["hueco", "deducido", "defecto", "usuario", "calculo", "dato"];

/**
 * La hoja de revisión del cuaderno (D23): cada celda con su origen y cada hueco
 * con su motivo, por apartados, para que el titular vea lo que firma. NO se
 * imprime en la copia de la Junta. Los valores y los nombres de las columnas van
 * en español, como el cuaderno (D15); el resto, en el idioma de quien mira.
 */
export function CuadernoRevisionSheet({ open, onOpenChange, revision }: { open: boolean; onOpenChange: (o: boolean) => void; revision: HojaRevision }) {
  const { t } = useTranslation();
  const secciones = useMemo(() => {
    const m = new Map<string, HojaRevision["celdas"]>();
    for (const c of revision.celdas) {
      const l = m.get(c.seccion) ?? [];
      l.push(c);
      m.set(c.seccion, l);
    }
    return [...m.entries()];
  }, [revision]);

  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title={t("cuadernoJunta.revision.title")} subtitle={t("cuadernoJunta.revision.subtitle")}>
      <div data-testid="cuaderno-revision" className="space-y-4 py-1">
        <div className="flex flex-wrap gap-1.5">
          {ORIGENES.map((o) => (
            <span
              key={o}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
              style={{ borderColor: `${COLOR[o]}55`, color: COLOR[o], backgroundColor: `${COLOR[o]}0F` }}
            >
              {t(`cuadernoJunta.revision.origin.${o}`)} · {revision.resumen[o]}
            </span>
          ))}
        </div>

        {secciones.map(([seccion, celdas]) => (
          <section key={seccion} className="space-y-1">
            <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
              {t("cuadernoJunta.revision.section", { section: seccion })}
            </h3>
            <ul className="divide-y rounded-xl border bg-card">
              {celdas.map((c, i) => (
                <li key={`${c.fila ?? ""}-${c.campo}-${i}`} className="flex items-start gap-2 px-3 py-1.5 text-[12px]">
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-foreground">
                      {c.etiqueta}
                      {c.contexto ? <span className="font-normal text-muted-foreground"> · {c.contexto}</span> : null}
                    </span>
                    <span className="block break-words text-muted-foreground">
                      {c.origen === "hueco"
                        ? t(`cuadernoJunta.revision.reason.${c.motivo ?? "falta"}`)
                        : c.valor || t("cuadernoJunta.revision.noValue")}
                    </span>
                  </span>
                  <span
                    className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                    style={{ color: COLOR[c.origen], backgroundColor: `${COLOR[c.origen]}14` }}
                  >
                    {t(`cuadernoJunta.revision.origin.${c.origen}`)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* CU9: lo que la persona cambió o añadió en el documento (los partes no cambian). */}
        {(revision.documento?.length ?? 0) > 0 && (
          <section data-testid="cuaderno-revision-documento" className="space-y-1">
            <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("cuadernoJunta.revision.document")}</h3>
            <ul className="divide-y rounded-xl border bg-card">
              {revision.documento.map((f) => (
                <li key={f.fila} className="px-3 py-1.5 text-[12px]">
                  <span className="text-muted-foreground">3.1 · {f.fila + 1} · </span>
                  <span className="font-medium">{f.producto}</span>
                  {f.fecha ? <span className="text-muted-foreground"> · {f.fecha.split("-").reverse().join("/")}</span> : null}
                  {f.anadida ? <span className="text-muted-foreground"> · {t("cuadernoJunta.revision.added")}</span> : null}
                </li>
              ))}
            </ul>
          </section>
        )}

        {revision.excluidas.length > 0 && (
          <section className="space-y-1">
            <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("cuadernoJunta.revision.excluded")}</h3>
            <ul className="divide-y rounded-xl border bg-card">
              {revision.excluidas.map((l) => (
                <li key={l.linea} className="px-3 py-1.5 text-[12px]">
                  <span className="font-medium">{l.producto}</span>
                  {l.fecha ? <span className="text-muted-foreground"> · {l.fecha.split("-").reverse().join("/")}</span> : null}
                  <span className="block text-muted-foreground">{l.motivo}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {revision.noSePuedenQuitar.length > 0 && (
          <section className="space-y-1">
            <h3 className="text-xs font-bold uppercase tracking-wide" style={{ color: COLOR.hueco }}>
              {t("cuadernoJunta.revision.cannotRemove")}
            </h3>
            <ul className="divide-y rounded-xl border bg-card">
              {revision.noSePuedenQuitar.map((l) => (
                <li key={l.linea} className="px-3 py-1.5 text-[12px] font-medium">
                  {l.producto}
                  {l.fecha ? <span className="font-normal text-muted-foreground"> · {l.fecha.split("-").reverse().join("/")}</span> : null}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </FormSheet>
  );
}
